import { useRouter } from "next/router";
import { useEffect, useState, useRef, useMemo } from "react";
import cls from "classnames";

import Carousel from "../../Carousel";
import Join from "../../channels/Join";
import About from "../../channels/About";
import Reviews from "../../channels/Reviews";
import Creator from "../../channels/Creator";
import ShareBar from "../../channels/ShareBar";
import JoinButton from "../../channels/JoinButton";
import JoinModals from "../../channels/JoinModals";
import Description from "../../channels/Description";
import ShareChannel from "../../channels/ShareChannel";
import CreatorNavBar from "components/Navbar/CreatorNavbar";
import CreatorFooter from "../../CreatorFooter";

import * as Track from "@qoohoo/common/utils/analytics/track";
import httpf from "../../../utils/frontend/httpf";
import { calcSeatStatus } from "@qoohoo/common/helpers/channel";
import useSessionStore from "../../../store/sessionStore";
import useHasHydrated from "@qoohoo/common/hooks/useHasHydrated";
import { button, pageview } from "@qoohoo/common/constants/analytics";
import useStore from "../../../store/store";
import Coupon from "../../coupon/Coupon";
import { LimitedSeatsRibbon } from "../../channels/Join";
import MembershipDurationLabel from "../../MembershipDurationLabel";
import ChannelNotAvailable from "../telegram/ChannelNotAvailable";
import SuperCheckoutJoinButton from "../SuperCheckoutJoinButton";
import { enableNewCheckoutForFeature } from "constants/constants";

const currentScreen = pageview.channel_details;

export default function DefaultChannelView({ channelDetails, customConfig }) {
  const router = useRouter();

  const {
    uid,
    name,
    short_url_flutter,
    category,
    is_paid,
    is_private,
    cta_button,
    has_limited_seats,
    total_seats_left,
    max_subscribers,
    creator,
    total_number_of_subscribers,
  } = channelDetails;
  
  const userData = useSessionStore((state) => state.userData);
  const setReadyToPay = useStore((state) => state.setReadyToPay);
  const storeLoading = useStore((state) => state.loading);
  const [showAppDownloadModal, setShowAppDownloadModal] = useState(false);

  const hasHydrated = useHasHydrated();

  const [userChannelDetail, setUserChannelDetail] = useState({});
  const [modalOpen, setModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const creatorRef = useRef(null);

  const isSubscribed = userChannelDetail.is_channel_member;

  const isOneTimePlan =
    ["one_time", "multi_day_session", "free_subscription"].indexOf(
      channelDetails.type.toLowerCase()
    ) > -1;
  const hasMultiplePlans = channelDetails?.plans?.length > 1;

  const ctaButton = userChannelDetail?.cta_button ?? channelDetails?.cta_button;
  const isChannelArchived = channelDetails.is_archived;
  const isChannelEnded = channelDetails.is_ended;

  const isCreator = channelDetails?.creator?.uid === userData?.uid;
  const isCoCreator = useMemo(() => {
    const co_creator_uids = userChannelDetail?.co_creators?.map(
      (creator) => creator?.uid
    );

    const userId = userData?.uid;

    return co_creator_uids?.includes(userId);
  }, [userData?.uid, userChannelDetail.co_creators]);

  const seatStatus =
    !channelDetails.has_limited_seats ||
    ctaButton?.action === null ||
    isSubscribed ||
    isCreator ||
    isCoCreator ||
    isChannelArchived ||
    isChannelEnded
      ? ""
      : calcSeatStatus(max_subscribers, total_seats_left);

  const areFewSeatsLeft = seatStatus.includes("left");
  const areSeatsFull = seatStatus.includes("No seats");

  const prepareCouponResponse = (coupon) => {
    return {
      coupon,
      channel: channelDetails?.uid,
    };
  };

  const onCouponValidation = (data) => {
    setCouponState(data);
  };

  const [couponState, setCouponState] = useState({});

  const firstChannelPlan = channelDetails?.plans?.[0];

  let channelFee;

  if (isOneTimePlan) {
    channelFee = channelDetails?.channel_fee;
  } else {
    channelFee =
      firstChannelPlan?.amount_after_discount || firstChannelPlan?.amount;
  }

  const isLifetime = userChannelDetail?.duration?.toLowerCase() === "lifetime";

  const isFreeChannel =
    channelDetails.type.toLowerCase() === "free_subscription";

  const couponDiscount = isOneTimePlan
    ? couponState?.data
    : couponState.data?.[firstChannelPlan?.uid];
  const showCouponField =
    !isChannelEnded &&
    !isChannelArchived &&
    channelDetails?.is_paid &&
    !isCreator &&
    !isCoCreator &&
    (isSubscribed ? !(isLifetime || isOneTimePlan) : !areSeatsFull);

  const showJoinButton =
    (!isSubscribed ||
      isCreator ||
      isCoCreator ||
      (isSubscribed && !(isOneTimePlan || isLifetime)));

  const showSuperCheckoutButton = showJoinButton && !(isFreeChannel || isCreator || isCoCreator);

  const showShareChannel = isCreator || isCoCreator || !is_private;
  const showShareBar = showShareChannel || total_number_of_subscribers > 0;

  const showMembershipDurationLabel =
    isSubscribed && !isChannelArchived && !isFreeChannel;
  
  const showReviews = !(channelDetails.disable_reviews || customConfig?.hideUserReviews); 

  function handleButtonClick(isPaymentAction = true) {
    if (!userData?.uid) {
      setModalOpen(true);
    } else {
      if (!isPaymentAction) {
        setShowAppDownloadModal(true);
        setModalOpen(true);
      } else if (hasMultiplePlans) {
        setShowAppDownloadModal(false);
        setModalOpen(true);
      } else {
        setShowAppDownloadModal(false);
        setModalOpen(true);
        setReadyToPay(true);
      }
    }
  }

  function getUserChannelDetail() {
    setIsLoading(true);
    httpf
      .post(`/api/auth-channel-details`, {
        id: channelDetails.uid,
      })
      .then((res) => {
        setUserChannelDetail(res.data);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setIsLoading(false);
      });
  }

  const scrollToCreator = () => {
    Track.buttonClick(button.scroll_to_creator, {
      current_screen: currentScreen,
      url: `/by/${channelDetails.creator.username}`,
      uid: channelDetails.uid,
      name: channelDetails.name,
      "creator.uid": channelDetails.creator.uid,
      "creator.username": channelDetails.creator.username,
      "creator.full_name": channelDetails.creator.fullname,
      is_verified_creator: channelDetails.creator.is_verified_creator,
    });

    if (creatorRef && creatorRef.current) {
      creatorRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  function renderButtons() {
    let joinButton;
    const enableNewCheckout = enableNewCheckoutForFeature.regular_channel;
    
    if(!showJoinButton) return;

    if(enableNewCheckout && showSuperCheckoutButton) {
      joinButton = (
        <SuperCheckoutJoinButton
          customConfig={customConfig}
          channelDetails={channelDetails}
          userChannelDetail={userChannelDetail}
          ctaButton={ctaButton}
          fullWidth
          isCreator={isCreator}
          isCoCreator={isCoCreator}
          isLoading={!hasHydrated || isLoading || storeLoading}
          isDisabled={areSeatsFull}
          couponState={couponState}
          couponDiscount={couponDiscount}
          channelFee={channelFee}
          userData={userData}
        />
      )
    } else {
      joinButton = (<>
      {showCouponField && (
        <Coupon
          state={couponState}
          prepareResponse={prepareCouponResponse}
          onValidation={onCouponValidation}
          className="mb-4"
        />
      )}
      {showJoinButton && (
        <JoinButton
          channelDetails={channelDetails}
          userChannelDetail={userChannelDetail}
          ctaButton={ctaButton}
          handleButtonClick={handleButtonClick}
          fullWidth
          isCreator={isCreator}
          isCoCreator={isCoCreator}
          isLoading={!hasHydrated || isLoading || storeLoading}
          isDisabled={areSeatsFull}
          couponState={couponState}
          couponDiscount={couponDiscount}
          channelFee={channelFee}
          customConfig={customConfig}
        />
      )}
      </>)
    }
    return joinButton;
  }

  useEffect(() => {
    userData?.uid && getUserChannelDetail();
  }, [userData]);

  if (router.isFallback) {
    return (
      <div className="flex flex-col h-screen px-8 bg-[#19191d]">
        <div className="flex items-center justify-center flex-grow">
          Loading...
        </div>
        <CreatorFooter />
      </div>
    );
  }

  if (isChannelArchived) {
    const creatorProfileLink = `/by/${channelDetails.creator.username}`;
    return (
      <div className="w-full sm:mb-0 min-h-screen flex flex-col justify-between items-stretch">
        <div>
          <CreatorNavBar creator={creator} currentScreen={currentScreen}/>
          <ChannelNotAvailable creatorProfileLink={creatorProfileLink} />
        </div>
        <CreatorFooter />
      </div>
    );
  }

  return (
    <>
      <CreatorNavBar creator={creator} currentScreen={currentScreen}/>
      <MembershipDurationLabel
        isHeaderMode={true}
        isVisible={showMembershipDurationLabel}
        link={() => handleButtonClick(false)}
        linkLabel="Go to channel"
        userChannelDetail={userChannelDetail}
        colFormat={true}
      />
      <main className="relative px-5 pt-12 md:pt-[72px] pb-[84px] sm:pb-[100px] md:pb-[132px] md:px-6 overflow-hidden">
        <LimitedSeatsRibbon seatStatus={seatStatus} isMobile />
        <div
          className={cls(
            !!seatStatus && "mt-4 md:mt-0",
            "grid grid-cols-12 max-w-[684px] md:max-w-[1120px] mx-auto"
          )}
        >
          <section className="col-span-12 md:col-span-7">
            <div className="flex flex-col-reverse md:flex-col">
              <div className="relative max-w-[740px] md:max-w-full w-full md:mx-auto rounded-2xl mt-10 sm:mt-14 md:mt-0">
                <Carousel channelDetails={channelDetails} />
              </div>
              <Description
                channelDetails={channelDetails}
                scrollToCreator={scrollToCreator}
                customConfig={customConfig}
              />
            </div>
            <About channelDetails={channelDetails} />
            {
              showReviews && <Reviews channelDetails={channelDetails} />
            }
            {showShareBar && (
              <ShareBar
                channelDetails={channelDetails}
                customConfig={customConfig}
              />
            )}
            <div ref={creatorRef}>
              <Creator channelDetails={channelDetails} />
            </div>
          </section>
          <section className="hidden col-end-13 pl-6 md:col-start-8 xl:col-start-9 md:block">
            <Join
              channelDetails={channelDetails}
              userChannelDetail={userChannelDetail}
              getUserChannelDetail={getUserChannelDetail}
              handleButtonClick={handleButtonClick}
              isLoading={!hasHydrated || isLoading || storeLoading}
              isCreator={isCreator}
              isCoCreator={isCoCreator}
              seatStatus={seatStatus}
              areFewSeatsLeft={areFewSeatsLeft}
              areSeatsFull={areSeatsFull}
              prepareCouponResponse={prepareCouponResponse}
              onCouponValidation={onCouponValidation}
              couponState={couponState}
              couponDiscount={couponDiscount}
              showCouponField={showCouponField}
              showJoinButton={showJoinButton}
              showMembershipDurationLabel={showMembershipDurationLabel}
              isChannelEnded={isChannelEnded}
              channelFee={channelFee}
              userData={userData}
              customConfig={customConfig}
              showSuperCheckoutButton={showSuperCheckoutButton}
            />
            {showShareChannel && (
              <ShareChannel
                channelDetails={channelDetails}
                currentUsername={userData?.username}
              />
            )}
          </section>
        </div>
      </main>

      <CreatorFooter />

      <div
        className={cls(
          "sticky bottom-0 left-0 w-full bg-[#262C30]  px-7 pb-10  pt-4 z-10 flex flex-col justify-between items-center md:hidden shadow-channelButton",
          !showJoinButton && "!hidden"
        )}
      >
        {renderButtons()}
      </div>

      {modalOpen ? (
        <JoinModals
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          channelDetails={channelDetails}
          userChannelDetail={userChannelDetail}
          isSubscribed={isSubscribed}
          isPrivate={is_private}
          isCreator={isCreator}
          isCoCreator={isCoCreator}
          getUserChannelDetail={getUserChannelDetail}
          handleJoinButtonClick={handleButtonClick}
          onCouponValidation={onCouponValidation}
          couponState={couponState}
          setIsLoading={setIsLoading}
          isLoadingApiResponse={isLoading}
          showAppDownloadModal={showAppDownloadModal}
          customConfig={customConfig}
        />
      ) : null}
    </>
  );
}
